import {
  MAIN_WHATSAPP_CONTACT_ID,
  type WhatsAppContactId,
  whatsappContacts,
  whatsappRouting,
} from '@/config/whatsappContacts';

import { matchLongestPathKey } from './routeMatch';

/**
 * A seat with a number in it: what a WhatsApp control actually opens.
 *
 * `e164` is a plain `string` rather than `string | null`, which is the point of
 * resolving at all — a seat waiting on a number has already fallen back to the
 * main line by the time it is one of these, so no caller has to think about a
 * half-filled config. `null` from `resolveWhatsAppContact` means something
 * else entirely: WhatsApp is switched off site-wide.
 */
export type ResolvedWhatsAppContact = {
  /** The seat the number came from, which is not always the one asked for. */
  id: WhatsAppContactId;
  name: string;
  role: string;
  e164: string;
};

export type WhatsAppContactRequest = {
  /**
   * A section's own seat, which beats everything below it — a parts card
   * sitting on a sales page, say. Usually absent.
   */
  contactId?: WhatsAppContactId | null | undefined;
  /**
   * The seat the whole page has chosen from data it loaded, set through
   * `useSetPageWhatsAppContact`. Beats the route map, loses to `contactId`.
   */
  pageContactId?: WhatsAppContactId | null | undefined;
  /** Matched against `whatsappRouting`. */
  pathname: string;
};

/**
 * The seat a path belongs to, or null when no key covers it.
 *
 * Matching is by segment and the longest key wins, so the answer does not
 * depend on the order the map happens to be typed out in. That rule moved to
 * `utils/routeMatch.ts` on 2026-10-24, when the scroll tips started routing by
 * path as well and the alternative was a second copy of it; the behaviour is
 * unchanged. This function stays because it is what names `whatsappRouting` as
 * the map being searched — a caller asking for a *seat* should not have to
 * know which config holds them.
 */
export function matchWhatsAppRoute(pathname: string): WhatsAppContactId | null {
  return matchLongestPathKey(pathname, whatsappRouting);
}

/**
 * Which number to open, given where the visitor is and what the caller asked
 * for. Pure, so the routing can be read and tested without rendering MUI;
 * `useWhatsAppContact` is the three lines of React on top.
 *
 * Section override → page override → route map → main, and then one more
 * fallback: a seat whose `e164` is still `null` hands over to main as well, so
 * an unassigned seat in the config is a page that behaves as it did before
 * rather than a dead button.
 *
 * Returns null only for the kill switch — `company.whatsappE164` set to null,
 * which empties the main seat and is still the one way to strip every WhatsApp
 * control off the site at once.
 */
export function resolveWhatsAppContact({
  contactId,
  pageContactId,
  pathname,
}: WhatsAppContactRequest): ResolvedWhatsAppContact | null {
  const main = whatsappContacts[MAIN_WHATSAPP_CONTACT_ID];
  if (!main.e164) return null;

  const id = contactId ?? pageContactId ?? matchWhatsAppRoute(pathname) ?? MAIN_WHATSAPP_CONTACT_ID;
  const contact = whatsappContacts[id];

  if (!contact.e164) {
    return {
      id: MAIN_WHATSAPP_CONTACT_ID,
      name: main.name,
      role: main.role,
      e164: main.e164,
    };
  }

  return { id, name: contact.name, role: contact.role, e164: contact.e164 };
}
